<template>
  <div class="wrapper" part="wrapper" :class="classModifiers" :style="styles">
    <div class="background-underlay"></div>
    <sp-row class="sp-row" v-bind="props" part="row" align="stretch">
      <slot />
    </sp-row>
  </div>
</template>

<script>
import { parsePadding } from "../../utils/css";
import { row as componentProps } from "./props";
</script>

<script setup>
import { computed } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { toSystemColorVariable, toSystemOnColorLinkVariable, toSystemOnColorVariable } from "../../composables/color";
import { toBoolean } from "../../utils/props";

const { isSmScreen, isMdScreen } = useBreakpoints();

const props = defineProps({
  ...componentProps,
  fullbleed: {
    type: [Boolean, String],
    default: false,
  },
  color: {
    type: String,
    default: undefined,
  },
  margin: {
    type: String,
    default: undefined,
  },
  padding: {
    type: String,
    default: undefined,
  },
  position: {
    type: String,
    default: "relative",
    validator: (value) => ["relative", "static"].includes(value),
  },
});

const backgroundColor = computed(() => toSystemColorVariable(props.color));
const color = computed(() => toSystemOnColorVariable(props.color));
const linkColor = computed(() => toSystemOnColorLinkVariable(props.color));

const backgroundUnderlayDisplay = computed(() => (props.position === "static" ? "none" : "block"));

const paddingValues = computed(() => parsePadding(props.padding));
const hasPaddingValue = computed(() => Object.keys(paddingValues.value).length > 0);

const paddingStyles = computed(() => {
  if (!hasPaddingValue.value) {
    return {};
  }

  const { top, right, bottom, left } = paddingValues.value;

  return {
    "--padding-top": top,
    "--padding-right": right,
    "--padding-bottom": bottom,
    "--padding-left": left,
  };
});

const styles = computed(() => ({ ...paddingStyles.value }));

const classModifiers = computed(() => ({
  "--full-bleed": toBoolean(props.fullbleed),
  "--has-color": props.color !== "none",
  "--is-sm-screen": isSmScreen.value,
  "--is-md-screen": isMdScreen.value,
  "--has-padding": hasPaddingValue.value,
}));
</script>

<style lang="scss">
:host {
  --full-bleed-vw: 100vw;
  display: block;
}

:host([height="100%"]) {
  height: 100%;
}
</style>

<style scoped lang="scss">
.wrapper {
  --sp-grid-border-radius: var(--sp-comp-grid-layout-row-border-radius, 0);
  --padding: var(--padding-top) var(--padding-right) var(--padding-bottom) var(--padding-left);
  --max-sm: var(--sp-comp-layout-row-padding-max-sm, 1.5rem);
  --sm-padding-top: min(var(--padding-top), var(--sp-comp-layout-row-padding-sm-top, var(--max-sm)));
  --sm-padding-right: min(var(--padding-right), var(--sp-comp-layout-row-padding-sm-right, var(--max-sm)));
  --sm-padding-bottom: min(var(--padding-bottom), var(--sp-comp-layout-row-padding-sm-bottom, var(--max-sm)));
  --sm-padding-left: min(var(--padding-left), var(--sp-comp-layout-row-padding-sm-left, var(--max-sm)));
  --sm-padding: var(--sm-padding-top) var(--sm-padding-right) var(--sm-padding-bottom) var(--sm-padding-left);

  margin: v-bind(margin);
  padding: var(--sp-comp-layout-row-padding, var(--padding));
  box-sizing: border-box;
  position: v-bind(position);
  z-index: 2;

  .background-underlay {
    display: v-bind(backgroundUnderlayDisplay);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    min-height: 40px;
    z-index: 1;
  }

  .sp-row {
    position: v-bind(position);
    z-index: 3;
  }

  &.--is-md-screen.--has-padding,
  &.--is-sm-screen.--has-padding {
    padding: var(--sp-comp-layout-row-padding-sm, var(--sm-padding));
  }

  &.--full-bleed {
    --sp-grid-border-radius: 0;

    .background-underlay {
      --computed-vw: var(--full-bleed-vw);
      --viewport-margin-inline: calc(var(--computed-vw) / 2 * -1 + 50%);
      border-radius: 0;
      width: var(--computed-vw);
      margin-inline: calc(var(--viewport-margin-inline));
    }
  }

  &.--has-color {
    --sp-comp-layout-row-color-link: v-bind(linkColor);

    .background-underlay {
      background-color: v-bind(backgroundColor);
      border-radius: var(--sp-grid-border-radius);
    }

    .sp-row {
      color: v-bind(color);
      border-radius: var(--sp-grid-border-radius);
    }
  }
}
</style>
